import React, { useState, useEffect } from 'react';
import axios from 'axios';
import IDCardPreview from '../components/IDCardPreview';
import { jsPDF } from "jspdf";

// Interfaces
interface NurseBasic {
  id: number;
  full_name: string;
  emp_id: string;
  designation: string;
  id_designation?: string;
  photo_url?: string;
  id_card_front_url?: string; // NEW
  id_card_back_url?: string;
}

const IDCardDashboard: React.FC = () => {
  const [nurses, setNurses] = useState<NurseBasic[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedNurseId, setSelectedNurseId] = useState<number | null>(null);

  // Fetch nurses on load
  useEffect(() => {
    const fetchNurses = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const response = await axios.get(`${API_BASE_URL}/api/nurses/`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        setNurses(response.data);
      } catch (error) {
        console.error('Failed to fetch nurses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNurses();
  }, []);

  // Filter and Sort logic
  const filteredNurses = nurses
    .filter(nurse => 
      nurse.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nurse.emp_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

  // --- SECURE DOWNLOAD HELPER ---
  const forceDownload = async (url: string, filename: string) => {
    try {
      // Fetch the image data directly
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create a temporary local URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a fake anchor tag, click it, and destroy it
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback just in case: open in new tab
      window.open(url, '_blank');
    }
  };

  // --- PDF DOWNLOAD HELPER ---
  const downloadAsPDF = async (frontUrl: string, backUrl: string, filename: string) => {
    try {
      // Helper to fetch images securely and convert to Base64 for the PDF generator
      const fetchAsBase64 = async (url: string) => {
        const response = await fetch(`${url}?t=${new Date().getTime()}`); // Cache buster
        const blob = await response.blob();
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      };

      // 1. Fetch both images
      const frontBase64 = await fetchAsBase64(frontUrl);
      const backBase64 = await fetchAsBase64(backUrl);

      // 2. Create a PDF sized EXACTLY to a standard CR80 ID Card (in inches)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [2.125, 3.375] 
      });

      // 3. Add Front Image (Page 1)
      pdf.addImage(frontBase64, 'JPEG', 0, 0, 2.125, 3.375);
      
      // 4. Add Back Image (Page 2)
      pdf.addPage([2.125, 3.375], 'portrait');
      pdf.addImage(backBase64, 'JPEG', 0, 0, 2.125, 3.375);

      // 5. Download!
      pdf.save(`${filename}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Page Header (Clean & Simple) */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.85rem', color: '#0f172a', fontWeight: 700 }}>ID Card Generator</h1>
        <p style={{ margin: '0.25rem 0 0 0', color: '#64748b', fontSize: '1rem' }}>Search and print PVC identification cards for staff members.</p>
      </div>

      {/* Main Data Card */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        
        {/* Card Toolbar */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#ffffff' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b', fontWeight: 600 }}>Staff Directory</h2>
          
          <div style={{ position: 'relative', width: '320px' }}>
            <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '1.1rem' }}>🔍</span>
            <input 
              type="text" 
              placeholder="Search by name or Emp ID..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%', 
                padding: '0.65rem 1rem 0.65rem 2.5rem', 
                borderRadius: '8px', 
                border: '1px solid #cbd5e1', 
                outline: 'none',
                backgroundColor: '#f8fafc',
                fontSize: '0.95rem',
                color: '#334155',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Nurse List Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '1rem 1.5rem', color: '#475569', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employee</th>
                <th style={{ padding: '1rem 1.5rem', color: '#475569', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Emp ID</th>
                <th style={{ padding: '1rem 1.5rem', color: '#475569', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Designation</th>
                <th style={{ padding: '1rem 1.5rem', color: '#475569', fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>Loading staff data...</td></tr>
              ) : filteredNurses.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No staff found matching "{searchTerm}"</td></tr>
              ) : (
                filteredNurses.map((nurse, index) => {
                  const isGenerated = !!nurse.id_card_front_url;
                  
                  return (
                  <tr key={nurse.id} style={{ borderBottom: index === filteredNurses.length - 1 ? 'none' : '1px solid #f1f5f9', transition: 'background-color 0.2s' }}>
                    
                    {/* Employee Profile */}
                    <td style={{ padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '2px solid #fff', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                        {nurse.photo_url ? <img src={nurse.photo_url} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <span style={{fontSize: '1.2rem'}}>👤</span>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{nurse.full_name}</span>
                        {/* Status Badge */}
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: isGenerated ? '#10b981' : '#94a3b8', marginTop: '2px' }}>
                          {isGenerated ? '✅ Generated' : '⏳ Pending'}
                        </span>
                      </div>
                    </td>
                    
                    <td style={{ padding: '1rem 1.5rem', color: '#64748b', fontWeight: 500 }}>{nurse.emp_id || '-'}</td>
                    
                    <td style={{ padding: '1rem 1.5rem', color: '#64748b' }}>
                      {
                        (nurse.designation && nurse.designation.trim() !== "" && nurse.designation !== "null") ? nurse.designation : 
                        (nurse.id_designation && nurse.id_designation.trim() !== "" && nurse.id_designation !== "null") ? nurse.id_designation : 
                        '-'
                      }
                    </td>
                    
                    <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        
                        {/* Generate Button (Primary) */}
                        <button 
                          onClick={() => setSelectedNurseId(nurse.id)}
                          style={{
                            backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', 
                            padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer',
                            fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                            transition: 'all 0.2s', fontSize: '0.85rem'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.color = 'white'; }}
                          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                        >
                          ⚙️ {isGenerated ? 'View/Edit Card' : 'Generate'}
                        </button>

                        {/* Download Buttons (Only show if card exists) */}
                        {isGenerated && (
                          <>
                            <button 
                              onClick={() => forceDownload(nurse.id_card_front_url!, `${nurse.emp_id || nurse.full_name}_idcard_Front.jpg`)}
                              style={{ backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                            >
                              ⬇️ Front
                            </button>
                            
                            <button 
                              onClick={() => forceDownload(nurse.id_card_back_url!, `${nurse.emp_id || nurse.full_name}_idcard_Back.jpg`)}
                              style={{ backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                            >
                              ⬇️ Back
                            </button>
                            <button 
                              onClick={() => downloadAsPDF(nurse.id_card_front_url!, nurse.id_card_back_url!, `${nurse.emp_id || nurse.full_name}_ID_Card`)}
                              style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s' }}
                              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                            >
                              📄 PDF
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- ID Card Preview Modal --- */}
      {selectedNurseId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '2rem', backdropFilter: 'blur(4px)' }}>
             <div style={{ backgroundColor: '#f8fafc', borderRadius: '16px', width: '950px', maxWidth: '100%', maxHeight: '95vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                 
                 <IDCardPreview 
                    nurseId={selectedNurseId} 
                    onClose={() => setSelectedNurseId(null)} 
                    onEditProfile={(id) => {
                      console.log("Navigate to edit profile for ID:", id);
                    }}
                 />

             </div>
        </div>
      )}

    </div>
  );
};

export default IDCardDashboard;